// Модель глобального поиска в шапке (пакет 8, п.12): когда запрос можно слать на сервер и как
// сложить ответ в плоский список для клавиатуры. Без React — проверяется отдельными тестами.
import type { SearchResponse } from "../hub/hubApi";

/** Запрос короче — на сервер не уходит (Go тоже ответит 400). */
export const SEARCH_MIN_CHARS = 3;
/** Для телефона нужно хотя бы столько цифр (меньше — «полсети»). */
export const SEARCH_MIN_DIGITS = 4;
/** Пауза после последней нажатой клавиши, мс. */
export const SEARCH_DEBOUNCE_MS = 300;

const PHONE_LIKE = /^[0-9+\-\s()]+$/;

export interface ParsedSearch {
  /** Запрос без пробелов по краям. */
  query: string;
  /** Достаточно длинный, чтобы искать. */
  ready: boolean;
  /** Похож на номер телефона (только цифры и знаки номера). */
  phone: boolean;
  /** Сколько цифр ещё нужно набрать для поиска гостя (0 — хватает или это не номер). */
  digitsLeft: number;
  /** Сколько символов ещё нужно набрать (0 — хватает). */
  charsLeft: number;
}

export function parseSearch(raw: string): ParsedSearch {
  const query = raw.trim();
  const chars = Array.from(query).length;
  const phone = query !== "" && PHONE_LIKE.test(query);
  const digits = phone ? query.replace(/\D/g, "").length : 0;
  return {
    query,
    ready: chars >= SEARCH_MIN_CHARS,
    phone,
    digitsLeft: phone && digits < SEARCH_MIN_DIGITS ? SEARCH_MIN_DIGITS - digits : 0,
    charsLeft: Math.max(0, SEARCH_MIN_CHARS - chars),
  };
}

/** Подсказка под полем, пока искать рано; null — можно искать. */
export function searchHint(parsed: ParsedSearch): string | null {
  if (parsed.query === "") return null;
  if (!parsed.ready) {
    const n = parsed.charsLeft;
    return `Введите ещё ${n} ${n === 1 ? "символ" : "символа"}`;
  }
  return null;
}

export type SearchGroupKey = "campaigns" | "guests" | "promos" | "templates";

export interface SearchItem {
  /** Уникальный ключ для списка и aria-activedescendant. */
  key: string;
  group: SearchGroupKey;
  title: string;
  subtitle: string;
  path: string;
}

export interface SearchGroup {
  key: SearchGroupKey;
  label: string;
  items: SearchItem[];
}

export const SEARCH_GROUP_LABEL: Record<SearchGroupKey, string> = {
  campaigns: "Кампании",
  guests: "Гости",
  promos: "Промокоды",
  templates: "Шаблоны",
};

const CAMPAIGN_STATUS: Record<string, string> = {
  active: "Идёт",
  paused: "На паузе",
  scheduled: "Запланирована",
  sending: "Отправляется",
  draft: "Черновик",
  pending_approval: "На согласовании",
  done: "Завершена",
  stopped: "Остановлена",
  cancelled: "Отменена",
};

const CHANNEL: Record<string, string> = { sms: "SMS", email: "Письмо", push: "Пуш" };

/** Группы результатов в порядке показа; пустые группы не возвращаются. */
export function searchGroups(res: SearchResponse | undefined): SearchGroup[] {
  if (!res) return [];
  const groups: SearchGroup[] = [
    {
      key: "campaigns",
      label: SEARCH_GROUP_LABEL.campaigns,
      items: res.campaigns.map((c) => ({
        key: `campaign:${c.id}`,
        group: "campaigns",
        title: c.name,
        subtitle: CAMPAIGN_STATUS[c.status] ?? c.status,
        path: c.path,
      })),
    },
    {
      key: "guests",
      label: SEARCH_GROUP_LABEL.guests,
      items: res.guests.map((g) => ({
        key: `guest:${g.id}`,
        group: "guests",
        title: g.phone,
        subtitle: [g.name, g.city].filter(Boolean).join(" · "),
        path: g.path,
      })),
    },
    {
      key: "promos",
      label: SEARCH_GROUP_LABEL.promos,
      items: res.promos.map((p) => ({
        key: `promo:${p.path}:${p.code}`,
        group: "promos",
        title: p.code,
        subtitle: `${p.status === "assigned" ? "Выдан" : "Свободен"} · серия «${p.pool}»`,
        path: p.path,
      })),
    },
    {
      key: "templates",
      label: SEARCH_GROUP_LABEL.templates,
      items: res.templates.map((t) => ({
        key: `template:${t.id}`,
        group: "templates",
        title: t.name,
        subtitle: [CHANNEL[t.channel] ?? t.channel, t.builtin ? "готовый" : "из библиотеки"].join(" · "),
        path: t.path,
      })),
    },
  ];
  return groups.filter((g) => g.items.length > 0);
}

/** Плоский список в порядке показа — по нему ходят стрелки. */
export function flattenSearch(groups: SearchGroup[]): SearchItem[] {
  return groups.flatMap((g) => g.items);
}

/** Следующая позиция стрелками: по кругу, -1 — ничего не выбрано. */
export function moveSelection(current: number, delta: 1 | -1, total: number): number {
  if (total <= 0) return -1;
  if (current < 0) return delta === 1 ? 0 : total - 1;
  return (current + delta + total) % total;
}

/** Путь результата безопасен для router.navigate: только внутри SMS-центра. */
export function safeSearchPath(path: string): string | null {
  return /^\/[A-Za-z0-9\-_/?=&%.]*$/.test(path) && !path.startsWith("//") ? path : null;
}
